
import { z } from "zod";
import DOMPurify from "dompurify";

// Email validation schema
export const emailSchema = z.string()
  .email("Invalid email format")
  .max(254, "Email too long")
  .trim()
  .toLowerCase();

// Phone validation schema
export const phoneSchema = z.string()
  .regex(/^[\+]?[1-9][\d]{0,15}$/, "Invalid phone number format")
  .max(20, "Phone number too long")
  .optional();

// Name validation schema
export const nameSchema = z.string()
  .min(1, "Name is required")
  .max(100, "Name too long")
  .regex(/^[a-zA-Z\s\-'\.]+$/, "Name contains invalid characters")
  .trim();

// Company validation schema
export const companySchema = z.string()
  .max(200, "Company name too long")
  .trim()
  .optional();

// Meeting link slug validation
export const slugSchema = z.string()
  .min(3, "Slug must be at least 3 characters")
  .max(50, "Slug too long")
  .regex(/^[a-z0-9-]+$/, "Slug can only contain lowercase letters, numbers, and hyphens")
  .refine(slug => !slug.startsWith('-') && !slug.endsWith('-'), "Slug cannot start or end with hyphen");

// URL validation schema
export const urlSchema = z.string()
  .url("Invalid URL format")
  .max(2048, "URL too long")
  .optional();

// Message/description validation
export const messageSchema = z.string()
  .max(2000, "Message too long")
  .trim()
  .optional();

// Sanitize HTML content to prevent XSS
export function sanitizeHtml(content: string): string {
  return DOMPurify.sanitize(content, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'ol', 'ul', 'li', 'a'],
    ALLOWED_ATTR: ['href', 'target'],
    ALLOW_DATA_ATTR: false
  });
}

// Sanitize plain text
export function sanitizeText(text: string): string {
  return text
    .trim()
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .substring(0, 2000); // Limit length
}

// Validate and sanitize booking form data
export const bookingFormSchema = z.object({
  booker_name: nameSchema,
  booker_email: emailSchema,
  booker_company: companySchema,
  booker_phone: phoneSchema,
  message: messageSchema.transform(msg => msg ? sanitizeText(msg) : undefined),
  booking_date: z.string().datetime()
});

// Validate and sanitize meeting link data
export const meetingLinkSchema = z.object({
  name: z.string().min(1, "Name is required").max(100, "Name too long").trim(),
  slug: slugSchema,
  description: messageSchema.transform(desc => desc ? sanitizeHtml(desc) : undefined),
  duration: z.number().min(15).max(480), // 15 minutes to 8 hours
  energy_preference: z.enum(['power', 'focus', 'drift', 'any']),
  custom_location: z.string().max(500, "Location too long").trim().optional(),
  custom_redirect_url: urlSchema,
  booking_message: messageSchema.transform(msg => msg ? sanitizeHtml(msg) : undefined),
  intent_prompt_text: messageSchema.transform(prompt => prompt ? sanitizeHtml(prompt) : undefined)
});

// Rate limiting store (in-memory for demo, should use Redis in production)
const rateLimitStore = new Map<string, { count: number; resetTime: number }>();

export function checkRateLimit(identifier: string, maxRequests: number, windowMs: number): boolean {
  const now = Date.now();
  const key = identifier;
  const existing = rateLimitStore.get(key);

  if (!existing || now > existing.resetTime) {
    rateLimitStore.set(key, { count: 1, resetTime: now + windowMs });
    return true;
  }

  if (existing.count >= maxRequests) {
    return false;
  }

  existing.count++;
  return true;
}

// Security headers for forms
export const securityHeaders = {
  'X-Content-Type-Options': 'nosniff',
  'X-Frame-Options': 'DENY',
  'X-XSS-Protection': '1; mode=block',
  'Referrer-Policy': 'strict-origin-when-cross-origin'
};
